/** @format */

import React, { useState } from "react";
import { withFormik, Field } from "formik";
import Select, {components, StylesConfig} from "react-select";
import { formatSpaceSqFt, propertyName } from "../Utils";
import WithModal from "./WithModal";
import { sendError } from "../Analytics";
import { API_BASE_API } from "../../OldApp";
import { Button, Text } from "@chakra-ui/react";
import AbsoluteLoadingOverlay from "../AbsoluteLoadingOverlay";

// Formik pieces come almost directly from their examples.

const spaceToSelectOption = (s) => ({
  value: s.sourceid,
  label: `Suite ${s.suite}`,
  labelLong: `Suite ${s.suite} (${formatSpaceSqFt(s)})`,
});

const PreferredContactMethodSelect = (props) => {
  return (
    <div
      style={{
        margin: "1rem 0 0 0",
        display: "flex",
        justifyContent: "space-between",
      }}
    >
      <label htmlFor="contactMode">
        Preferred Contact Method
      </label>
      <div className="radioField">
        <div className="radio">
          <label className="option">
            <Field type="radio" name="contactMethod" value="Email" onClick={()=>props.onBlur("contactMethod", true)}/>
            Email
          </label>
        </div>
        <div className="radio">
          <label className="option">
            <Field type="radio" name="contactMethod" value="Call" onClick={()=>props.onBlur("contactMethod", true)}/>
            Call
          </label>
        </div>
        <div className="radio">
          <label className="option">
            <Field type="radio" name="contactMethod" value="Text" onClick={()=>props.onBlur("contactMethod", true)}/>
            Text
          </label>
        </div>
      </div>
    </div>
  );
};
// control height 50px here to match the rest of the form (different from 40px on TenantInfoForm)
const inputHeight = "50px";
const inputBorderRadius = "40px";
const selectCustomStyles = (props) => {
  // noinspection JSUnusedGlobalSymbols // Not sure why IDEA thinks these object keys are unused globals
  const customStyles = {
    multiValueLabel: (base) => ({ ...base, color: "#3697B5" }),
    menu: (base) => ({ ...base, marginTop: "2px" }),
    menuList: (base) => ({ ...base, paddingTop: "0", paddingBottom: "0" }),
    control: (base) => ({
      ...base,
      height: inputHeight,
      borderRadius: inputBorderRadius,
      borderColor: props.error && props.touched ? "red" : "#032845",
    }),
  };
  return customStyles;
};
const SuiteSelect = (props) => {
  // noinspection JSUnusedGlobalSymbols // Not sure why IDEA thinks these object keys are unused globals
  const customStyles = selectCustomStyles(props)
  return (
    <div style={{ margin: "1rem 0" }}>
      <label htmlFor="suite">Suite (if applicable)</label>
      <Select
        id="suite"
        options={props.options}
        isMulti={true}
        onChange={(value) => props.onChange("suite", value)}
        onBlur={() => props.onBlur("suite", true)}
        value={props.value}
        closeMenuOnSelect={props.options.length === 1}
        components={{
          Option: (optionProps) => (
            <components.Option {...optionProps}>
              {optionProps.data.labelLong}
            </components.Option>
          ),
          NoOptionsMessage: (optionProps) => (
            <components.NoOptionsMessage {...optionProps}>
              No available suites
            </components.NoOptionsMessage>
          ),
        }}
        styles={customStyles}
      />
      {!!props.error && props.touched && (
        <div className="input-feedback">{props.error}</div>
      )}
    </div>
  );
};

const ContactForm = (props) => {
  const {
    property,
    values,
    touched,
    errors,
    dirty,
    isSubmitting,
    handleSubmit,
    handleReset,
    setFieldValue,
    setFieldTouched,
  } = props;

  const Errors = ({ id, ...props }) =>
    (props.errors[id] && props.touched[id] && (
      <div className="input-feedback">{props.errors[id]}</div>
    )) ||
    null;

  const TextInput = (m) => (
    /* For some reason, calling this as <TextInput> blurs after each char, but calling as a function in {} works fine? */
    <div className="contactFormInput contactFormInput2" key={m.id} style={{flex:"1"}}>
      <label htmlFor={m.id} className={m.required ? "label-required" : ""}>
        {m.label}
      </label>
      <Field type="text" name={m.id} placeholder={m.placeholder}>
        {({ field, form }) => (
          <input
            type="text"
            {...field}
            className={
              form.errors[m.id] && form.touched[m.id]
                ? "text-input error"
                : "text-input"
            }
          />
        )}
      </Field>
      <Errors id={m.id} {...props} />
    </div>
  );
  return (
    <div className="contactForm contactformtwoWrapper">
      <div className="propertyColumns">
        <div className="propertyLeftColumn">
          <div className="propertyAddressCard">
            <h1 className="propertyName">
              {property.externalName || property.name}
            </h1>
          </div>
        </div>
      </div>
      {props.submissionResult && <p>{props.submissionResult}</p>}
      {!props.submissionResult && (
        <form className="contactFormForm" onSubmit={handleSubmit}>
          <div style={{display: "flex", gap:"1rem"}}>
            {[
              {
                id: "first_name",
                required: true,
                placeholder: "First name",
                label: "First Name",
              },{
                id: "last_name",
                required: true,
                placeholder: "Last name",
                label: "Last Name",
              },
            ].map(TextInput)}
          </div>
          {[
            {
              id: "email",
              required: true,
              placeholder: "Email Address",
              label: "Email Address",
            },
            {
              id: "phone",
              required: true,
              placeholder: "Phone Number",
              label: "Phone Number",
            },
          ].map(TextInput)}
          <div className="contactFormInput">
            <PreferredContactMethodSelect
              onBlur={setFieldTouched}
            />
            <Errors id={"contactMethod"} {...props} />
          </div>
          <div className="contactFormInput">
            <SuiteSelect
              value={values.suite}
              options={property.availableSpaces.map(spaceToSelectOption)}
              onChange={setFieldValue}
              onBlur={setFieldTouched}
              error={errors.suite}
              touched={touched.suite}
            />
          </div>
          {[
            {
              id: "practiceType",
              required: false,
              placeholder: "Enter your practice type",
              label: "Practice Type",
            },
          ].map(TextInput)}
          <div className="contactFormInput">
            <label htmlFor="comments">Comments</label>
            <Field
              type="text"
              name="comments"
              placeholder="Any additional comments"
            >
              {({ field, form }) => (
                <textarea
                  {...field}
                  className={
                    form.errors["comments"] && form.touched["comments"]
                      ? "text-input error"
                      : "text-input"
                  }
                />
              )}
            </Field>

            <Errors f="comments" {...props} />
          </div>
          <div className="contactFormButton">
            <Text as={"span"} color={"DarkBlue"} style={{ float: "left" }}>
              * required fields
            </Text>
          </div>
          <div className="contactFormButton">
            <Button
              type="button"
              className="resetButton bk-button"
              onClick={handleReset}
              disabled={!dirty || isSubmitting}
              fontWeight={"400"}
              h={"48px"}
              borderRadius={"30px"}
              textTransform={"math-auto"}
              border={"1px solid transparent !important"}
            >
              Reset
            </Button>
            <Button
              className="contactButton bk-button"
              fontWeight={"400"}
              h={"48px"}
              borderRadius={"30px"}
              color={"#1A202C !important"}
              bgColor={"BrightTurquoise !important"}
              textTransform={"math-auto"}
              border={"1px solid transparent !important"}
              type={"submit"}
              isDisabled={isSubmitting}
            >
              Submit
            </Button>
          </div>
          {isSubmitting && <AbsoluteLoadingOverlay />}
        </form>
      )}
    </div>
  );
};

const postData = (url, data) => {
  // Default options are marked with *
  return fetch(url, {
    body: JSON.stringify(data), // must match 'Content-Type' header
    cache: "no-cache", // *default, no-cache, reload, force-cache, only-if-cached
    //credentials: 'same-origin', // include, same-origin, *omit
    headers: {
      "content-type": "application/json",
    },
    method: "POST", // *GET, POST, PUT, DELETE, etc.
    mode: "cors", // no-cors, cors, *same-origin
    redirect: "follow", // *manual, follow, error
    referrer: "no-referrer", // *client, no-referrer
  }).then((response) => response.json());
};

const EnhancedForm = withFormik({
  mapPropsToValues: (props) => {
    let suite = [];
    //if (props.property.availableSpaces.length === 1) {
    //  let s = props.property.availableSpaces[0];
    //  suite.push(spaceToSelectOption(s));
    //}
    return {
      property_sourceid: props.property.sourceid,
      property_wtweb_id: props.property.wtweb_id,
      property_name: propertyName(props.property),
      first_name: "",
      last_name: "",
      email: "",
      phone: "",
      contactMethod: "",
      practiceType: "",
      comments: "",
      suite,
    };
  },
  /* Building a Yup schema with a cyclic dependency and the noSortEdges "escape hatch" worked in Chrome, but somehow blew up in IE.
    I tried to figure it out, but eventually had to give up and write a simple custom validation.
     */
  validate: (values, props) => {
    let errors = {};

    // copied from yup https://raw.githubusercontent.com/jquense/yup/cd407f53cadd2c94fab55c3c2d536245ac7c7919/src/string.js
    const rEmail =
      /^((([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+(\.([a-z]|\d|[!#\$%&'\*\+\-\/=\?\^_`{\|}~]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])+)*)|((\x22)((((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(([\x01-\x08\x0b\x0c\x0e-\x1f\x7f]|\x21|[\x23-\x5b]|[\x5d-\x7e]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(\\([\x01-\x09\x0b\x0c\x0d-\x7f]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF]))))*(((\x20|\x09)*(\x0d\x0a))?(\x20|\x09)+)?(\x22)))@((([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|\d|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))\.)+(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])|(([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])([a-z]|\d|-|\.|_|~|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])*([a-z]|[\u00A0-\uD7FF\uF900-\uFDCF\uFDF0-\uFFEF])))$/i; // eslint-disable-line
    // Really basic 10-digit validation
    const rPhone = /^\D*\d{3}\D*\d{3}\D*\d{4}\D*$/;

    if (!values.first_name) {
      errors.first_name = "Please enter your first name";
    }
    if (!values.last_name) {
      errors.last_name = "Please enter your last name";
    }

    if (values.email && !rEmail.test(values.email)) {
      errors.email = "Invalid email address";
    }
    if (values.phone && !rPhone.test(values.phone)) {
      errors.phone = "Invalid phone number";
    }
    if (!values.email && !values.phone) {
      errors.email = "Please enter either your email address or phone number";
      errors.phone = "Please enter either your email address or phone number";
    }

    if (!values.email && values.contactMethod === "Email") {
      errors.contactMethod =
        "Please provide a valid email address to use this contact method";
    }
    if (
      !values.phone &&
      (values.contactMethod === "Call" || values.contactMethod === "Text")
    ) {
      errors.contactMethod =
        "Please provide a valid phone number to use this contact method";
    }

    return errors;
  },
  /*validationSchema: yup.object().shape(
    {
      name: yup.string().required("Please enter your name"),
      email: yup.string()
        .email("Invalid email address")
        .when("phone", {
          is: phone => !phone,
          then: yup.string().required(
            "Please enter either your email address or phone number"
          ),
        }),

      phone: yup.string()
        .matches(/^\D*\d{3}\D*\d{3}\D*\d{4}\D*$/, "Invalid phone number")
        .when("email", {
          is: email => !email,
          then: yup.string().required(
            "Please enter either your email address or phone number"
          )
        })
    },
    ["email", "phone"]
  ),*/
  handleSubmit: (values, { props, setSubmitting }) => {
    const payload = {
      ...values,
      suite:
        values.suite &&
        values.suite.length > 0 &&
        values.suite.map((t) => t.label),
    };
    postData(`${API_BASE_API}contactForm`, payload)
      .then((data) => {
        props.captureResult(data.msg);
        setSubmitting(false);
        //        props.closeModal && props.closeModal();
      })
      .catch((error) => {
        console.error(error);
        sendError(error);
        props.captureResult("An error has occurred. Please try again later.");
        setSubmitting(false);
      });
  },
  displayName: "ContactForm", // helps with React DevTools
})(ContactForm);

const ContactFormWrapper = (props) => {
  const [submissionResult, setSubmissionResult] = useState();

  return (
    <EnhancedForm
      {...props}
      captureResult={(r) => setSubmissionResult(r)}
      submissionResult={submissionResult}
    />
  );
};

const ContactFormButton = WithModal(({ property, children, ...props }) => (
  <>
    {children({ showModal: props.showModal })}
    {props.modalContent(<ContactFormWrapper property={property} {...props} />, {
      showCloseIcon: true,
      trackPath: `${property.wtweb_id}/contact`,
      modalStyles: { width: "600px", maxWidth: "calc(100% - 2.4rem)" },
      closeOnOverlayClick: false,
    })}
  </>
));

export default ContactFormButton;
